import type { BalanceConfig, TowerConfig } from "@bastion/data";
import type { DamageType, Point, Random } from "../types";

export interface DamageResult {
  /** 对目标造成的最终伤害（已取整，§45） */
  amount: number;
  /** floor() 之前的原始值——对 UI/测试有用 */
  rawAmount: number;
  isCritical: boolean;
  damageType: DamageType;
}

/**
 * 纯函数伤害管线（战斗规则 §7, §8, §10）：
 *
 *   基础攻击（attack 或 magic，由 tower.damageType 决定）
 *     × balance.globalDamageMultiplier
 *     × 100 / (100 + defense)          [仅物理伤害，§7]
 *     × 100 / (100 + magicResistance)  [仅魔法伤害，§8]
 *     × critDamage 若触发暴击          [暴击在减免后应用，§10]
 *     → floor()                        [§45]
 *
 * 暴击使用 critRate 作为 0-100 的百分比：注入的 Random roll 与
 * critRate / 100 比较（CLAUDE.md §5.2，规则 §44）。
 */
export function calculateDamage(
  tower: TowerConfig,
  towerLevel: number,
  target: {
    defense: number;
    magicResistance: number;
    hp: number;
  },
  context: {
    random?: Random;
    balance: BalanceConfig;
    /** DoT tick 或技能施加伤害时覆盖基础伤害 */
    damageOverride?: number;
    damageMultiplier?: number;
    damageTypeOverride?: DamageType;
  },
): DamageResult {
  const upgradeIndex = Math.max(1, towerLevel);
  const upgrade = tower.upgrades[upgradeIndex - 1] ?? tower.upgrades[0];
  const damageType = context.damageTypeOverride ?? tower.damageType;
  const baseDamage =
    context.damageOverride ??
    upgrade.attack ??
    (damageType === "magic" ? tower.stats.magic : tower.stats.attack);
  let damage =
    baseDamage * (context.damageMultiplier ?? 1) * (context.balance.globalDamageMultiplier ?? 1);

  // 伤害类型减免（§7 / §8）。物理伤害由防御减免，
  // 魔法伤害由魔法抗性减免；两种属性不会交叉应用。
  if (damageType === "magic") {
    damage = (damage * 100) / (100 + target.magicResistance);
  } else {
    damage = (damage * 100) / (100 + target.defense);
  }

  // 暴击：roll against critRate (0-100)，在减免后乘算（§10）。
  let isCritical = false;
  const critRate = tower.stats.critRate;
  const critDamage = tower.stats.critDamage;
  if (critRate > 0 && context.random && context.random.next() * 100 < critRate) {
    isCritical = true;
    damage *= critDamage;
  }

  const rawAmount = damage;
  const amount = Math.max(1, Math.floor(damage));
  return { amount, rawAmount, isCritical, damageType };
}

/**
 * 按状态效果规则 §2.1 计算控制效果修正系数：
 *   修正系数 = (1 + 命中率 / 100) / (1 + 抗性 / 100)
 * 最终效果和最终持续时间均乘以该修正系数。
 */
export function calculateControlModifier(accuracy: number, resistance: number): number {
  return (1 + accuracy / 100) / (1 + resistance / 100);
}

/**
 * 按战斗规则 §16 计算控制命中概率：
 *   概率 = baseChance × 100 / (100 + max(0, 抗性 - 命中率))
 * 限制在 0..1 范围内。
 */
export function calculateControlChance(
  baseChance: number,
  sourceAccuracy: number,
  targetResistance: number,
): number {
  const effectiveResistance = Math.max(0, targetResistance - sourceAccuracy);
  const chance = baseChance * (100 / (100 + effectiveResistance));
  return Math.min(1, Math.max(0, chance));
}

/**
 * 在 `position` 周围 `range` 范围内寻找最佳目标。
 * 平局通过稳定、确定性的键打破（见策略），使相同输入始终产生相同选择——
 * 这对确定性回放至关重要。
 */
export type TargetStrategy = "first" | "strongest" | "closest";

export function selectTarget(
  position: Point,
  range: number,
  candidates: Array<{ id: string; position: Point; hp: number; alive: boolean }>,
  strategy: TargetStrategy = "first",
): string | null {
  const inRange = candidates.filter(
    (c) => c.alive && Math.hypot(c.position.x - position.x, c.position.y - position.y) <= range,
  );
  if (inRange.length === 0) return null;

  if (strategy === "strongest") {
    return pickStableById(
      inRange.map((c) => c.id),
      (id) => enemyScore(inRange, id),
      true, // highest hp wins
    );
  }
  if (strategy === "closest") {
    return pickStableById(
      inRange.map((c) => c.id),
      (id) => {
        const enemy = inRange.find((c) => c.id === id);
        if (!enemy) return Infinity;
        return Math.hypot(enemy.position.x - position.x, enemy.position.y - position.y);
      },
    );
  }
  // 'first' — deterministic tie-break by id to avoid Set-order nondeterminism
  const sorted = [...inRange].sort((a, b) => (a.id < b.id ? -1 : 1));
  const winner = sorted.find((c) => c.hp > 0);
  return winner ? winner.id : null;
}

function enemyScore(
  inRange: Array<{ id: string; position: Point; hp: number; alive: boolean }>,
  id: string,
): number {
  return inRange.find((c) => c.id === id)?.hp ?? 0;
}

/** 返回分数最低（'strongest' 时为最高）的项的 id。 */
function pickStableById(
  ids: string[],
  scoreOf: (id: string) => number,
  preferHighest = false,
): string | null {
  if (ids.length === 0) return null;
  let bestId = ids[0];
  let bestScore = scoreOf(bestId);
  for (let i = 1; i < ids.length; i++) {
    const candidateScore = scoreOf(ids[i]);
    const better = preferHighest ? candidateScore > bestScore : candidateScore < bestScore;
    if (better || (candidateScore === bestScore && ids[i] < bestId)) {
      bestId = ids[i];
      bestScore = candidateScore;
    }
  }
  return bestId;
}

export interface TowerLevelStats {
  attack: number;
  magic: number;
  defense: number;
  magicResistance: number;
  accuracy: number;
  resistance: number;
  attackSpeed: number;
  critRate: number;
  critDamage: number;
  range: number;
  splashRadius: number;
  chainCount: number;
}

export function getTowerLevelStats(config: TowerConfig, level: number): TowerLevelStats {
  const upgradeIndex = Math.max(1, level);
  const upgrade = config.upgrades[upgradeIndex - 1] ?? config.upgrades[0];
  return {
    attack:
      config.damageType === "physical"
        ? (upgrade.attack ?? config.stats.attack)
        : config.stats.attack,
    magic:
      config.damageType === "magic" ? (upgrade.attack ?? config.stats.magic) : config.stats.magic,
    defense: config.stats.defense,
    magicResistance: config.stats.magicResistance,
    accuracy: config.stats.accuracy,
    resistance: config.stats.resistance,
    attackSpeed: upgrade.attackSpeed ?? config.attackSpeed,
    critRate: config.stats.critRate,
    critDamage: config.stats.critDamage,
    range: upgrade.range ?? config.range,
    splashRadius: (upgrade as { splashRadius?: number }).splashRadius ?? config.splashRadius ?? 0,
    chainCount: (upgrade as { chainCount?: number }).chainCount ?? config.chainCount ?? 0,
  };
}
